import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    naver?: any;
    navermap_authFailure?: () => void;
  }
}

const SCRIPT_ID = 'naver-map-sdk';

let sdkPromise: Promise<any> | null = null;
let geocoderPromise: Promise<any> | null = null;

function loadNaverMaps(clientId: string): Promise<any> {
  if (window.naver?.maps) return Promise.resolve(window.naver);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}&submodules=geocoder`;
    script.async = true;
    script.onload = () => {
      const naver = window.naver;
      if (!naver?.maps) {
        sdkPromise = null;
        reject(new Error('naver maps not available'));
        return;
      }
      resolve(naver);
    };
    script.onerror = () => {
      sdkPromise = null;
      script.remove();
      reject(new Error('failed to load naver maps'));
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
}

/** geocoder 서브모듈은 본 스크립트 로드 이후 비동기로 준비됨 — 지도 표시와 분리해서 대기 */
function waitGeocoder(naver: any): Promise<any> {
  if (naver.maps.Service) return Promise.resolve(naver);
  if (geocoderPromise) return geocoderPromise;
  geocoderPromise = new Promise((resolve) => {
    const prev = naver.maps.onJSContentLoaded;
    naver.maps.onJSContentLoaded = () => {
      prev?.();
      resolve(naver);
    };
  });
  return geocoderPromise;
}

type Props = {
  /** 네이버 클라우드 플랫폼 Maps Application 의 Client ID */
  clientId: string;
  address: string;
  title: string;
  /** 지오코딩 실패 시 사용할 좌표 */
  fallback: { lat: number; lng: number };
  zoom?: number;
};

export default function NaverMap({ clientId, address, title, fallback, zoom = 16 }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(!clientId);

  useEffect(() => {
    if (!clientId) {
      console.warn('[NaverMap] clientId 가 없어 지도를 표시할 수 없습니다.');
      return;
    }
    let cancelled = false;

    // 키 인증 실패(미등록 도메인 등) 시 네이버 SDK가 호출하는 전역 콜백
    window.navermap_authFailure = () => {
      console.warn('[NaverMap] 인증 실패 — NCP 콘솔의 Client ID / Web 서비스 URL 등록을 확인하세요.');
      if (!cancelled) setFailed(true);
    };

    loadNaverMaps(clientId)
      .then((naver) => {
        if (cancelled || !ref.current) return;
        const center = new naver.maps.LatLng(fallback.lat, fallback.lng);
        const map = new naver.maps.Map(ref.current, {
          center,
          zoom,
          zoomControl: true,
          zoomControlOptions: { position: naver.maps.Position.TOP_RIGHT },
        });
        const marker = new naver.maps.Marker({ position: center, map, title });

        waitGeocoder(naver).then(() => {
          if (cancelled) return;
          naver.maps.Service.geocode({ query: address }, (status: any, response: any) => {
            if (cancelled || status !== naver.maps.Service.Status.OK) return;
            const item = response?.v2?.addresses?.[0];
            if (!item) return;
            const pos = new naver.maps.LatLng(Number(item.y), Number(item.x));
            map.setCenter(pos);
            marker.setPosition(pos);
          });
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, address, title, fallback.lat, fallback.lng, zoom]);

  if (failed) {
    return (
      <a
        href={`https://map.naver.com/p/search/${encodeURIComponent(address)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${title} 네이버 지도에서 보기`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          background: '#f4f4f4',
          color: '#03c75a',
          fontWeight: 600,
        }}
      >
        네이버 지도에서 보기
      </a>
    );
  }

  return <div ref={ref} role="region" aria-label={title} style={{ width: '100%', height: '100%' }} />;
}
